import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, API, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/cast/Layout";
import { AuthSky } from "@/components/cast/AuthSky";

const HERO = "https://static.prod-images.emergentagent.com/jobs/2bfd65d8-4762-4154-9373-440077215f5a/images/08ceb7feaca809d95ded7ecbbf68066d46d118bb35c97f43b58c7f3e03534bab.jpeg";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/auth/google";
  window.location.href = `${API}/auth/google/start?redirect_uri=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage({ mode }: { mode: "login" | "register" }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", name: "", birth_date: "" });
  const [error, setError] = useState(() => {
    const e = new URLSearchParams(window.location.search).get("error");
    return e === "apple" ? "Apple sign-in didn't complete. Please try again." : e === "suspended" ? "This account has been suspended" : "";
  });
  const [busy, setBusy] = useState(false);
  const [appleOn, setAppleOn] = useState(false);
  useEffect(() => { api.get("/auth/apple/enabled").then((r) => setAppleOn(r.data.enabled)).catch(() => {}); }, []);
  if (user) return <Navigate to={user.onboarded ? "/shore" : "/onboarding"} replace />;
  const isReg = mode === "register";

  const submit = async (e: any) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(isReg ? "/auth/register" : "/auth/login", isReg ? f : { email: f.email, password: f.password });
      setUser(data);
      nav(data.onboarded ? "/shore" : "/onboarding");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img src={HERO} alt="Fishermen pulling up full nets at dawn" className="ken-burns h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B]/85 via-[#1E293B]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-amber-50">
          <p className="font-scripture text-2xl italic leading-relaxed">“Launch out into the deep, and let down your nets for a draught.”</p>
          <p className="mt-2 text-sm font-semibold tracking-wide text-amber-200">Luke 5:4 · KJV</p>
        </div>
      </div>
      <div className="relative flex flex-col justify-center overflow-hidden px-6 py-12 sm:px-16">
        <AuthSky />
        <div className="relative mx-auto w-full max-w-md rounded-[2rem] bg-white/70 p-6 shadow-xl ring-1 ring-white/60 backdrop-blur-md sm:p-8" data-testid="auth-card">
          <Logo />
          <h1 className="mt-10 font-display text-4xl font-semibold text-slate-900 sm:text-5xl">{isReg ? "Join Cast" : "Welcome back"}</h1>
          <p className="mt-3 text-slate-600">{isReg ? "Accounts are for adults 18+. Parents can add child profiles after signing up." : "Sign in to continue your journey."}</p>
          <Button data-testid="google-login-button" variant="outline" onClick={googleLogin} className="mt-8 h-12 w-full rounded-full border-[#E2DBCE] bg-white text-base font-semibold">
            <svg viewBox="0 0 24 24" className="mr-2 h-5 w-5"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.2H12v4.2h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2-1.9 3.2-4.7 3.2-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.7c-1 .7-2.2 1-3.7 1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.8A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.8 14.1a6.6 6.6 0 0 1 0-4.2V7.1H2.1a11 11 0 0 0 0 9.8z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.2 1.6l3.1-3.1A11 11 0 0 0 2.1 7.1l3.7 2.8C6.7 7.3 9.1 5.4 12 5.4z"/></svg>
            Continue with Google
          </Button>
          {appleOn && (
            <Button data-testid="apple-login-button" onClick={() => { window.location.href = `${API}/auth/apple/start`; }} className="mt-3 h-12 w-full rounded-full bg-black text-base font-semibold text-white hover:bg-black/85">
              <svg viewBox="0 0 24 24" className="mr-2 h-5 w-5 fill-white"><path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.77-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.38-3.69zM14.1 5.84c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.67 1.37-.58.67-1.1 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.27z"/></svg>
              Continue with Apple
            </Button>
          )}
          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400"><span className="h-px flex-1 bg-[#E2DBCE]" />or email<span className="h-px flex-1 bg-[#E2DBCE]" /></div>
          <form onSubmit={submit} className="space-y-4">
            {isReg && (<div><Label htmlFor="name">Your name (kept private)</Label><Input id="name" data-testid="register-name-input" value={f.name} onChange={set("name")} required className="mt-1.5 h-11 bg-white" /></div>)}
            <div><Label htmlFor="email">Email</Label><Input id="email" type="email" data-testid="auth-email-input" value={f.email} onChange={set("email")} required className="mt-1.5 h-11 bg-white" /></div>
            <div><div className="flex items-center justify-between"><Label htmlFor="password">Password</Label>{!isReg && <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs font-semibold text-[#C86D51] hover:underline">Forgot password?</Link>}</div><Input id="password" type="password" minLength={isReg ? 8 : undefined} data-testid="auth-password-input" value={f.password} onChange={set("password")} required className="mt-1.5 h-11 bg-white" /></div>
            {isReg && (<div><Label htmlFor="bd">Birth date</Label><Input id="bd" type="date" data-testid="register-birthdate-input" value={f.birth_date} onChange={set("birth_date")} required className="mt-1.5 h-11 bg-white" /></div>)}
            {error && <p data-testid="auth-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="auth-submit-button" className="h-12 w-full rounded-full bg-[#C86D51] text-base font-semibold text-white hover:bg-[#B35A3F]">
              {busy ? "Please wait…" : isReg ? "Create account" : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            {isReg ? "Already have an account? " : "New to Cast? "}
            <Link data-testid="auth-switch-link" to={isReg ? "/login" : "/register"} className="font-semibold text-[#C86D51] hover:underline">{isReg ? "Sign in" : "Create an account"}</Link>
          </p>
          <p className="mt-4 text-center text-xs text-slate-500">By continuing you agree to our <Link to="/terms" data-testid="auth-terms-link" className="underline hover:text-slate-700">Terms of Service</Link> and <Link to="/privacy" data-testid="auth-privacy-link" className="underline hover:text-slate-700">Privacy Policy</Link>.</p>
        </div>
      </div>
    </div>
  );
}
